/**
 * UnifiedTimeline — one chronological view of everything we know about a
 * customer. Drop-in for the Coach Lead Detail screen (replaces the older
 * fragmented Communication Log + activity feed).
 *
 * Pulls /api/coach/leads/{user_id}/timeline which aggregates:
 *   - in-app messages (in/out)
 *   - outbound emails (email_log)
 *   - assessment request submission + confirmation
 *   - bookings (created, checked-in, no-show, cancelled, late-cancel)
 *   - audit events (status changes, waiver, billing actions)
 *   - billing alerts (failed-charge + recovered)
 *   - waiver signature
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
    Mail,
    MessageSquare,
    Calendar,
    CheckCircle2,
    AlertTriangle,
    FileSignature,
    DollarSign,
    Activity,
    Loader2,
    Filter,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
    { key: "all", label: "All" },
    { key: "email", label: "Emails" },
    { key: "message", label: "Messages" },
    { key: "booking_form", label: "Forms" },
    { key: "checkin", label: "Check-ins" },
    { key: "billing_alert", label: "Billing" },
    { key: "audit", label: "System" },
];

const ICON_MAP = {
    mail: Mail,
    message: MessageSquare,
    calendar: Calendar,
    check: CheckCircle2,
    warn: AlertTriangle,
    sign: FileSignature,
    dollar: DollarSign,
};

const KIND_COLOR = {
    email: "text-brand-navy-300 border-brand-navy-800/60",
    message: "text-emerald-300 border-emerald-900/60",
    booking_form: "text-brand-red border-red-900/60",
    checkin: "text-emerald-300 border-emerald-900/60",
    no_show: "text-amber-300 border-amber-900/60",
    late_cancel: "text-amber-300 border-amber-900/60",
    status_change: "text-zinc-300 border-zinc-700",
    waiver_signed: "text-brand-red border-red-900/60",
    billing_alert: "text-rose-300 border-rose-900/60",
    payment_resolved: "text-emerald-300 border-emerald-900/60",
    audit: "text-zinc-400 border-zinc-700",
};

function fmtTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const today = new Date();
        const same = d.toDateString() === today.toDateString();
        if (same) {
            return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
        }
        return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch {
        return iso;
    }
}

export default function UnifiedTimeline({ userId, limit = 200 }) {
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        if (!userId) return;
        setData(null);
        let alive = true;
        axios.get(`${API}/coach/leads/${userId}/timeline`, {
            params: { limit },
            withCredentials: true,
        })
            .then((r) => { if (alive) setData(r.data); })
            .catch(() => { if (alive) setData({ events: [], total: 0, error: true }); });
        return () => { alive = false; };
    }, [userId, limit]);

    const filtered = useMemo(() => {
        if (!data?.events) return [];
        if (filter === "all") return data.events;
        // Group booking/audit/status_change/payment_resolved under "audit"
        if (filter === "audit") {
            return data.events.filter((e) => ["audit", "status_change", "payment_resolved", "waiver_signed"].includes(e.kind));
        }
        return data.events.filter((e) => e.kind === filter);
    }, [data, filter]);

    if (!data) {
        return (
            <div className="border border-zinc-900 bg-zinc-950 p-5 flex items-center gap-2 text-zinc-400 text-sm" data-testid="timeline-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading timeline…
            </div>
        );
    }

    return (
        <div data-testid="unified-timeline" className="border border-zinc-900 bg-zinc-950">
            <div className="px-4 py-3 border-b border-zinc-900 flex items-center justify-between gap-2 flex-wrap">
                <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red inline-flex items-center gap-2">
                    <Activity className="w-3 h-3" /> Timeline
                    <span className="text-zinc-500 ml-1">({data.total || 0})</span>
                </div>
                <div className="flex items-center gap-1 flex-wrap text-[10px] tracking-[0.18em] uppercase">
                    <Filter className="w-3 h-3 text-zinc-500 mr-1" />
                    {FILTERS.map((f) => (
                        <button
                            type="button"
                            key={f.key}
                            data-testid={`timeline-filter-${f.key}`}
                            onClick={() => setFilter(f.key)}
                            className={`px-2 py-1 border transition-colors ${
                                filter === f.key
                                    ? "border-brand-red text-brand-red"
                                    : "border-zinc-800 text-zinc-400 hover:text-white"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>
            {filtered.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-zinc-500" data-testid="timeline-empty">
                    Nothing yet under this filter.
                </div>
            ) : (
                <ul className="divide-y divide-zinc-900">
                    {filtered.map((e) => (
                        <TimelineRow key={e.id} event={e} />
                    ))}
                </ul>
            )}
        </div>
    );
}

function TimelineRow({ event }) {
    const Icon = ICON_MAP[event.icon_hint] || Activity;
    const colorClass = KIND_COLOR[event.kind] || "text-zinc-400 border-zinc-700";
    return (
        <li
            data-testid={`timeline-row-${event.id}`}
            data-kind={event.kind}
            className="px-4 py-3 flex items-start gap-3 hover:bg-zinc-900/30 transition-colors"
        >
            <div className={`mt-0.5 w-7 h-7 rounded-full border flex items-center justify-center shrink-0 bg-black ${colorClass}`}>
                <Icon className="w-3.5 h-3.5" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                    <div className="text-sm text-white truncate">{event.title || event.kind}</div>
                    <div className="text-[11px] text-zinc-500 shrink-0">{fmtTime(event.at)}</div>
                </div>
                {event.subtitle && (
                    <div className="text-[12px] text-zinc-400 mt-0.5 truncate">{event.subtitle}</div>
                )}
            </div>
            {event.direction === "in" && (
                <span className="text-[9px] tracking-[0.2em] uppercase text-emerald-400 shrink-0 mt-1.5">in</span>
            )}
            {event.direction === "out" && (
                <span className="text-[9px] tracking-[0.2em] uppercase text-brand-navy-300 shrink-0 mt-1.5">out</span>
            )}
        </li>
    );
}
